/**
 * Flache Blattliste des normalisierten Berichts und die daraus abgeleiteten Aggregate.
 *
 * Ein „Blatt" ist eine vollständige Kombination Datei[→Struktur]→Prüfregel; alles, was die
 * Berichtsansichten anzeigen, leitet sich daraus ab. Die Spaltenansicht pivotiert die Blätter je
 * Ebene zu Auswahllisten ({@link aggregateColumnItems}), die Kartenansicht gruppiert sie zu einem
 * Baum. Deshalb liegt die Blattbildung hier – rein, ohne Angular und ohne UI-Zustand.
 */

import {NormalizedReport, ReportGroup, ReportRule, ReportStructure} from './normalized-report.model';

/** Eine vollständige Kombination Datei[→Struktur]→Prüfregel. */
export interface ReportLeaf {
  file: ReportGroup;
  structure?: ReportStructure;
  rule: ReportRule;
}

/** Aggregiertes Item einer Gruppierungsebene (Datei/Struktur/Regel). */
export interface ReportColumnItem {
  name: string;
  status: 'success' | 'warning' | 'error' | 'unknown';
  /** Aggregiertes Ergebnis, oder `null` wenn keine der zusammengefassten Regeln bewertet ist. */
  percent: number | null;
  fileFormat?: string;
  shortCode?: string;
  description?: string;
}

// 'unknown' (z. B. nicht relevante Datei, fehlende Information) liegt bewusst über 'warning': ein
// unklares Ergebnis darf im aggregierten Status nicht hinter einem echten Teilerfolg verschwinden.
const SEVERITY = {success: 0, warning: 1, unknown: 2, error: 3} as const;

/**
 * Rundet einen Prozentwert so, dass die Extremwerte 100 und 0 aussagekräftig bleiben.
 *
 * `Math.round` allein erzeugte widersprüchliche Berichte: ein Aggregat von 99,5 % erschien als
 * „100 %", obwohl in der Spalte darunter eine Prüfregel mit 99 % stand. 100 % bedeutet im
 * Bericht „vollständig erfüllt" und 0 % „nichts erfüllt" – beide dürfen also nur entstehen,
 * wenn der Wert sie exakt erreicht, sonst wird auf 99 bzw. 1 gekappt.
 */
export function roundPercentPreservingExtremes(value: number): number {
  const clamped = Math.max(0, Math.min(value, 100));
  const rounded = Math.round(clamped);
  if (rounded >= 100 && clamped < 100) return 99;
  if (rounded <= 0 && clamped > 0) return 1;
  return rounded;
}

/**
 * Mittelwert über die BEWERTETEN Prozentwerte einer Menge; `null`, wenn keiner bewertet ist.
 *
 * `null` ist hier bedeutungstragend und nicht dasselbe wie `0`: „nichts bewertet" darf nicht als
 * „0 % erfüllt" erscheinen (und ein unbewertetes Ergebnis auch nicht als 50 % einfließen, wie es
 * die frühere Gewichtung von `unknown` tat). Alle Adapter und Aggregate rechnen hierüber, damit
 * Spaltenansicht, Kartenansicht und Export denselben Nenner benutzen.
 *
 * Über {@link roundPercentPreservingExtremes} gilt dabei überall dieselbe Invariante: ein
 * Aggregat zeigt 100 % nur, wenn JEDER bewertete Kindwert 100 % ist, und 0 % nur, wenn jeder
 * 0 % ist. Ohne das zeigte die Prüfregel-Spalte 98 %, während jede Datei darunter 100 % auswies.
 */
export function averageRatedPercent(
  percents: ReadonlyArray<number | null | undefined>,
): number | null {
  const rated = percents.filter((percent): percent is number => percent != null);
  return rated.length > 0
    ? roundPercentPreservingExtremes(
      rated.reduce((sum, percent) => sum + percent, 0) / rated.length)
    : null;
}

/** Anzeigetext eines Prozentwerts; „–", wenn nichts bewertet wurde. */
export function percentLabel(percent: number | null | undefined): string {
  return percent == null ? '–' : `${percent} %`;
}

/** Der „schlechteste" Status einer Menge (error > unknown > warning > success). */
export function worstStatus(
  statuses: ReadonlyArray<'success' | 'warning' | 'error' | 'unknown'>,
): 'success' | 'warning' | 'error' | 'unknown' {
  return statuses.reduce<'success' | 'warning' | 'error' | 'unknown'>(
    (worst, status) => SEVERITY[status] > SEVERITY[worst] ? status : worst, 'success',
  );
}

/**
 * Baut die flache Blattliste. Die Strukturebene wird nur ausgewertet, wenn sie in `levelKeys`
 * vorkommt – sonst hängen die Regeln direkt an der Datei. Dateien ohne Positionen fallen auch bei
 * aktiver Strukturebene auf `file.rules` zurück, damit gemischte Berichte nichts verlieren.
 */
export function buildReportLeaves(
  report: NormalizedReport | null | undefined,
  levelKeys: ReadonlyArray<string>,
): ReportLeaf[] {
  const useStructure = levelKeys.includes('structure');
  const leaves: ReportLeaf[] = [];
  for (const file of report?.groups ?? []) {
    if (useStructure && (file.structures?.length ?? 0) > 0) {
      for (const structure of file.structures ?? []) {
        for (const rule of structure.rules ?? []) leaves.push({file, structure, rule});
      }
    } else {
      for (const rule of file.rules ?? []) leaves.push({file, rule});
    }
  }
  return leaves;
}

/** Der identifizierende Wert eines Blattes auf einer Ebene. */
export function leafValue(leaf: ReportLeaf, levelKey: string): string {
  if (levelKey === 'files') return leaf.file?.name ?? '';
  if (levelKey === 'structure') return leaf.structure?.name ?? '';
  if (levelKey === 'rules') return leaf.rule?.name ?? '';
  return '';
}

/**
 * Blätter, die zu den Selektionen ALLER Ebenen VOR der gegebenen passen. Damit zeigt jede Spalte
 * der Spaltenansicht nur die Items, die unter der bisherigen Auswahl noch erreichbar sind.
 */
export function leavesBefore(
  leaves: ReadonlyArray<ReportLeaf>,
  levelKeys: ReadonlyArray<string>,
  levelKey: string,
  selection: Record<string, string | undefined>,
): ReportLeaf[] {
  const idx = levelKeys.indexOf(levelKey);
  const prior = idx < 0 ? levelKeys : levelKeys.slice(0, idx);
  return leaves.filter(leaf => prior.every(key => leafValue(leaf, key) === selection[key]));
}

/**
 * Distinct Items einer Ebene, aggregiert über die übergebenen Blätter: Status = schlechtester
 * Status, Ergebnis = Mittelwert der Prozentwerte der zugehörigen Regeln. Kürzel/Beschreibung
 * hängen an der Prüfregel, das Dateiformat an der Datei – sie werden vom ersten Blatt der
 * jeweiligen Gruppe übernommen.
 */
export function aggregateColumnItems(
  leaves: ReadonlyArray<ReportLeaf>,
  levelKey: string,
): ReportColumnItem[] {
  const byName = new Map<string, {
    statuses: Array<'success' | 'warning' | 'error' | 'unknown'>; percents: Array<number | null>;
    fileFormat?: string; shortCode?: string; description?: string;
  }>();

  for (const leaf of leaves) {
    const name = leafValue(leaf, levelKey);
    let agg = byName.get(name);
    if (!agg) {
      agg = {
        statuses: [], percents: [],
        fileFormat: levelKey === 'files' ? (leaf.file.fileFormat || undefined) : undefined,
        shortCode: levelKey === 'rules' ? (leaf.rule.shortCode || undefined) : undefined,
        description: levelKey === 'rules' ? (leaf.rule.description || undefined) : undefined,
      };
      byName.set(name, agg);
    }
    agg.statuses.push(leaf.rule.status);
    // Unbewertetes bleibt unbewertet: als 0 % eingerechnet hätte eine nie geprüfte Regel das
    // Ergebnis der Datei nach unten gezogen.
    agg.percents.push(leaf.rule.percent);
  }

  const items = [...byName.entries()].map(([name, agg]) => ({
    name,
    status: worstStatus(agg.statuses),
    percent: averageRatedPercent(agg.percents),
    fileFormat: agg.fileFormat,
    shortCode: agg.shortCode,
    description: agg.description,
  }));

  // Dateien: höchstes Ergebnis zuerst, dann Dateiname. Prüfregeln: alphabetisch nach Kürzel
  // (numerisch-bewusst, z. B. BL_1_4 vor BL_1_11), dann nach Name. Andere Ebenen (z. B.
  // Positionen) behalten die Auftrittsreihenfolge der Blätter.
  if (levelKey === 'files') {
    // Unbewertete Dateien nach hinten (nicht wie 0 % dazwischen): sie sind keine schlechten
    // Ergebnisse, sondern gar keine.
    items.sort((a, b) => (b.percent ?? -1) - (a.percent ?? -1)
      || a.name.localeCompare(b.name, 'de', {numeric: true}));
  } else if (levelKey === 'rules') {
    items.sort((a, b) =>
      (a.shortCode ?? '').localeCompare(b.shortCode ?? '', 'de', {numeric: true})
      || a.name.localeCompare(b.name, 'de', {numeric: true}));
  }

  return items;
}

/**
 * Anzeigewert einer konfigurierten Spalte für ein aggregiertes Item.
 *
 * Die „Ergebnis"-Spalte (Key `rating`) zeigt bewusst immer den aggregierten Prozentwert: die
 * früheren Spalten „Bewertung" und „Ergebnis" wurden zu dieser einen zusammengeführt.
 */
export function columnItemValue(
  item: ReportColumnItem,
  levelKey: string,
  columnKey: string,
): string {
  if (columnKey === 'rating') return percentLabel(item.percent);
  if (levelKey === 'files') {
    if (columnKey === 'fileName') return item.name;
    if (columnKey === 'fileFormat') return item.fileFormat ?? '';
  } else if (levelKey === 'rules') {
    if (columnKey === 'name') return item.name;
    if (columnKey === 'shortCode') return item.shortCode ?? '';
    if (columnKey === 'description') return item.description ?? '';
  } else if (levelKey === 'structure') {
    if (columnKey === 'identifikator') return item.name;
  }
  return '';
}
